import React, { lazy, Suspense } from "react";

import profile1 from "@/assets/waggex/waggex-company-announcement-software-dashboard-india.webp?w=400&format=webp";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("./HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
const DynamicFeaturesSection = lazy(
  () => import("../components/common/DynamicFeaturesSection"),
);

import { FaBullhorn, FaBell, FaSyncAlt } from "react-icons/fa";
import StatsSection from "../components/common/StatsSection";
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";

const referralFeatures = [
  "Referral code access",
  "Referral history",
  "Referral tracking",
  "Referral benefit visibility",
  "Company referral management",
];

const adminFeatures = [
  {
    icon: FaBullhorn,
    title: "Send Announcements Instantly",
    description:
      "Share important company announcements, HR updates, policy changes, and notices with employees in just a few clicks.",
  },
  {
    icon: FaBell,
    title: "Notify Single or All Employees",
    description:
      "Send announcements to an individual employee, selected teams, departments, or broadcast updates across the entire organization.",
  },
  {
    icon: FaSyncAlt,
    title: "Keep Employees Updated",
    description:
      "Ensure employees never miss important HR, payroll, attendance, leave, holiday, or company announcements with timely notifications.",
  },
];
const brandingFeatures = [
  {
    id: 1,
    title: "Send to All Employees",
    desc: "Admin can create a company-wide announcement and send it to all employees at once.",
  },
  {
    id: 2,
    title: "Send to One Employee",
    desc: "Company can send a specific announcement or notice to an individual employee.",
  },
  {
    id: 3,
    title: "Title & Description",
    desc: "Create clear announcement messages with a title, description, and important instructions.",
  },
  {
    id: 4,
    title: "Admin Portal Management",
    desc: "All announcements are created and managed from the company admin portal.",
  },
  {
    id: 5,
    title: "Employee Notifications",
    desc: "Employees can receive and view announcements from their dashboard or mobile app.",
  },
  {
    id: 6,
    title: "Announcement History",
    desc: "Admins can track previously sent announcements and maintain communication records.",
  },
  {
    id: 7,
    title: "HR & Payroll Notices",
    desc: "Send salary updates, attendance reminders, leave policy updates, holiday notices, and compliance information.",
  },
  {
    id: 8,
    title: "Secure Communication",
    desc: "Announcements are shared inside Waggex to keep internal communication organized and secure.",
  },
];

const hrmsModules = [
  "Payroll Management",
  "Attendance Management",
  "Leave Management",
  "Shift Management",
  "Task Management",
  "Employee Management",
  "Reminder Management",
  "Tax & Form Management",
  "Reports & Analysis",
];

const companystepsFeatures = [
  {
    id: 1,
    title: "HR Policy Updates",
    desc: "Share changes in company policies, work rules, leave rules, or attendance guidelines.",
  },
  {
    id: 2,
    title: "Payroll Announcements",
    desc: "Notify employees about salary processing dates, payslip availability, tax forms, or payroll updates.",
  },
  {
    id: 3,
    title: "Attendance & Shift Notices",
    desc: "Inform employees about shift changes, attendance rules, late mark policies, or working day updates.",
  },
  {
    id: 4,
    title: "Holiday Announcements",
    desc: "Send festival holiday notices, weekly off changes, or special working day updates.",
  },
  {
    id: 5,
    title: "Company Events",
    desc: "Announce meetings, team events, training sessions, or company celebrations.",
  },
  {
    id: 6,
    title: "Individual Employee Notices",
    desc: "Send private updates or instructions to a specific employee when required.",
  },
];

const steps = [
  {
    id: 1,
    title: "Admin\nLogin",
    desc: "Company administrator logs in to the Waggex company portal to access the Announcement Management module.",
  },
  {
    id: 2,
    title: "Create Announcement",
    desc: "Admin creates an announcement by adding the title, message, and other important details.",
  },
  {
    id: 3,
    title: "Select\nAudience",
    desc: "Choose whether to send the announcement to a single employee, selected employees, or the entire organization.",
  },
  {
    id: 4,
    title: "Send\nAnnouncement",
    desc: "Publish the announcement through the Waggex platform for instant delivery to the selected recipients.",
  },
  {
    id: 5,
    title: "Employee Views Update",
    desc: "Employees receive and view the announcement from their Waggex dashboard or mobile application.",
  },
];

const Faqs1 = [
  {
    title: "What is Company Announcement Software?",
    content:
      "Company Announcement Software helps businesses share important workplace updates from one centralized platform. HR teams can instantly publish payroll notices, HR policies, attendance reminders, holiday announcements, company events, and other employee communications.",
  },
  {
    title: "Can I send announcements to selected employees or departments?",
    content:
      "Yes. You can send announcements to individual employees, specific departments, teams, or your entire workforce. This ensures the right message reaches the right people without unnecessary communication.",
  },
  {
    title: "What types of company announcements can I create?",
    content:
      "You can share HR policies, payroll updates, attendance reminders, holiday schedules, shift changes, office events, compliance notices, training updates, and other important workplace communications through one platform.",
  },
];

const Faqs2 = [
  {
    title: "Will employees receive instant notifications?",
    content:
      "Yes. Employees receive instant notifications through the Waggex Staff App whenever a new announcement is published, helping ensure important updates are seen without relying on emails or messaging apps.",
  },
  {
    title: "Can employees view previous announcements?",
    content:
      "Yes. Employees can access previously published announcements within the Staff App, making it easy to revisit company policies, payroll notices, event updates, and other important communications whenever needed.",
  },
  {
    title: "How does Company Announcement improve internal communication?",
    content:
      "By replacing scattered communication across emails, WhatsApp groups, and notice boards, Company Announcement provides one reliable place to share updates, reduce missed messages, and keep every employee informed consistently.",
  },
];

const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Company Announcement Software",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS",
    description:
      "Waggex Company Announcement allows HR admins to send policy updates, payroll notices, attendance reminders, holiday announcements, and company events to all employees or specific individuals from one centralized platform.",
    url: "https://www.waggex.com/company-announcements",
    featureList: [
      "Company announcement software India",
      "Employee announcement tool",
      "HR policy update notifications",
      "Send announcements to all employees",
      "Internal communication software India",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "193",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is Company Announcement in Waggex?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Company Announcement is a feature within the Waggex HRMS that allows HR admins and managers to send structured notices to all employees or specific individuals directly through the platform.",
        },
      },
      {
        "@type": "Question",
        name: "Can I send an announcement to only one employee?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex allows you to send announcements to all employees at once or target a single employee with a private notice for offer letters, warnings, salary revisions, or individual communications.",
        },
      },
      {
        "@type": "Question",
        name: "What type of announcements can I send?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Waggex supports HR policy updates, payroll notices, attendance reminders, shift announcements, holiday schedules, company events, compliance notices, and individual employee notifications.",
        },
      },
      {
        "@type": "Question",
        name: "Can I send an announcement to all employees?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The Send to All Employees option broadcasts a single announcement to your entire workforce at once, and every employee receives the update instantly through the Waggex Staff App.",
        },
      },
      {
        "@type": "Question",
        name: "Is this feature useful for field employees?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Field employees receive announcements directly through the Waggex Staff App on their mobile devices, making it ideal for teams working across multiple locations.",
        },
      },
      {
        "@type": "Question",
        name: "Is Company Announcement part of Waggex HRMS?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Company Announcement is fully integrated with Waggex HRMS and works alongside payroll, attendance, shift management, leave management, and employee records for seamless internal communication.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Waggex HRMS & Payroll Software",
    provider: {
      "@type": "Organization",
      name: "Waggex",
      url: "https://www.waggex.com/",
    },
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  },
];

const JourneyCard = ({ step }) => {
  return (
    <div
      className={`
        rounded-xl
        p-5
        min-h-37.5
        transition-all
        duration-300
        hover:-translate-y-1
        ${
          step.dark
            ? "bg-linear-to-br from-[#6F8898] to-[#10324F] text-white"
            : "bg-white/30 text-[#10324F]"
        }
        
      `}
      style={{
        boxShadow: `
          rgb(167 191 210 / 35%) -12px -12px 20px inset,
          rgb(170 188 202 / 20%) -18px -18px 30px
        `,
      }}
    >
      <div className="flex gap-3 mb-4">
        <div
          className={`
            h-8 w-8 rounded-md flex items-center justify-center
            text-sm font-semibold shrink-0
            ${step.dark ? "bg-white/30 text-[#10234F]" : "bg-[#10324F] text-white"}
          `}
        >
          {step.id}
        </div>

        <h2 className="font-semibold text-sm leading-6">{step.title}</h2>
      </div>

      <p
        className={`text-xs leading-6 ${
          step.dark ? "text-white" : "text-[#10324F]"
        }`}
      >
        {step.description}
      </p>
    </div>
  );
};

const journeySteps = [
  {
    id: "1",
    title: "Better Employee Communication",
    dark: false,
    description:
      "Keep employees informed about important updates without depending only on emails or messages",
  },
  {
    id: "2",
    title: "Faster HR Updates",
    dark: true,
    description:
      "HR teams can send announcements quickly from the company portal",
  },
  {
    id: "3",
    title: "Reduced Confusion",
    dark: false,
    description:
      "Employees receive clear and official communication from the company",
  },
  {
    id: "4",
    title: "Announcement Records",
    dark: true,
    description:
      "All announcements remain managed in one HRMS system for future reference and tracking",
  },
  {
    id: "5",
    title: "Useful for Growing Teams",
    dark: false,
    description:
      "Ideal for companies managing office staff, field employees, remote teams, and multiple departments",
  },
];

export default function CompanyAnnouncement() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      <Helmet>
        <title>Company Announcement & Employee Communication Software</title>
        <meta
          name="description"
          content="Send company announcements, HR policy updates, payroll notices, attendance reminders, and employee communications instantly through one centralized platform. Try Waggex free today"
        />
        <meta
          name="keywords"
          content="company announcement software,employee announcement software India,HR announcement tool for businesses,internal communication software India,send announcements to employees,company notification software,HR policy update software India,HRMS announcement feature,employee communication platform India
"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/company-announcements"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d1dfea] overflow-hidden py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Heading */}
              <h1 className="text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-[28px]">
                Company Announcement Software For Companies
              </h1>

              {/* Description */}
              <p className="service-list-item-text py-4 max-w-md sm:max-w-2xl mx-auto">
                Keep every employee informed without chasing emails, calls, or
                WhatsApp groups. Share company-wide announcements, policy
                updates, urgent notices, and workplace information instantly
                through one centralized platform, ensuring every employee
                receives the right message at the right time
              </p>

              {/* Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
                >
                  Start Free Today
                </button>

                <button
                  onClick={() => (window.location.href = "/contact-us")}
                  className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
                >
                  Book a Demo
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative flex justify-center items-center">
              {/* Phones Image */}
              <picture>
                <source media="(max-width: 639px)" srcSet={profile1} />

                <img
                  src={profile1}
                  width={400}
                  height={377}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  alt="Waggex company announcement software dashboard showing payslip management, employee face attendance and leave approval announcement features"
                  className="object-contain"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <section className="w-full bg-white px-6 py-10">
        <div className="max-w-6xl mx-auto mt-10 mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-6 text-center lg:text-left">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold leading-snug">
                Keep Every Employee Informed
              </h2>

              <p className="selfie-service-list-item-text py-4">
                Important workplace updates are often scattered across emails,
                WhatsApp groups, notice boards, and verbal conversations. As a
                result, employees miss critical information, communication
                becomes inconsistent, and HR spends valuable time repeating the
                same updates
              </p>

              <p className="selfie-service-list-item-text">
                Waggex centralizes employee communication by letting HR share
                announcements, payroll updates, policy changes, attendance
                reminders, and company news from one platform ensuring every
                message reaches the right employees on time
              </p>
            </div>

            {/* Right Cards */}
            <div className="space-y-5">
              {adminFeatures.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    style={{
                      boxShadow: `
                      rgb(167 191 210 / 40%) -15px -15px 20px inset,
                      rgb(170 188 202 / 30%) -20px -20px 30px
                    `,
                    }}
                    className="h-40 sm:h-32 flex gap-4 items-start bg-white border border-[#E5EDF5] rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 max-w-xl"
                  >
                    {/* Icon */}
                    <div className="flex items-center justify-center">
                      <Icon className="w-10 h-10 text-[#10324F]" />
                    </div>

                    {/* Content */}
                    <div>
                      <h2 className="text-[#10324F] font-semibold text-sm">
                        {item.title}
                      </h2>

                      <p className="selfie-service-list-item-text mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-[#F6F9FB] py-10 px-6">
        <div className="max-w-6xl mx-auto mt-10 mb-10">
          {/* Heading */}
          <div className="text-center max-w-5xl mx-auto">
            <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
              Share Important Updates Instantly
            </h2>
            <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
              Send HR announcements, payroll updates, policy changes, holiday
              notices, attendance reminders, event updates, and important
              company communications from one platform.
            </p>
          </div>
          <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {companystepsFeatures.map((item) => {
              // const Icon = item.icon;

              return (
                <div
                  key={item.id}
                  style={{
                    boxShadow: `
            rgb(167 191 210 / 30%) -15px -15px 20px inset,
            rgb(170 188 202 / 20%) -20px -20px 30px
          `,
                  }}
                  className="rounded-xl border border-[#E4ECF3] p-6 hover:-translate-y-1 transition-all duration-300"
                >
                  {/* Top Row */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                      {item.id}
                    </div>
                  </div>

                  {/* Content */}
                  <h2 className="text-sm sm:text-lg font-bold text-[#10324F] font-roboto">
                    {item.title}
                  </h2>

                  <p className="mt-1 text-sm leading-6 text-[#10324F] font-roboto">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="w-full bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto mt-10 mb-10">
          {/* Heading */}
          <div className="text-center max-w-5xl mx-auto">
            <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
              Everything You Need for Better Employee Communication
            </h2>

            <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
              Share HR announcements, payroll updates, attendance reminders,
              policy changes, and company news instantly through one centralized
              platform that keeps every employee informed
            </p>
          </div>

          {/* Cards */}
          <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
            {brandingFeatures.map((item) => (
              <div
                key={item.id}
                style={{
                  boxShadow: `
                  rgb(167 191 210 / 30%) -15px -15px 20px inset,
                  rgb(170 188 202 / 20%) -20px -20px 30px
                `,
                }}
                className="bg-white rounded-xl shadow-sm border border-[#E4ECF3] p-4 sm:p-6 flex gap-3 items-start"
              >
                {/* Number Box */}
                <div className=" mt-4 min-w-8.5 h-8.5 rounded-md bg-[#10324F] font-roboto text-white flex items-center justify-center text-sm font-bold shadow">
                  {item.id}
                </div>

                {/* Content */}
                <div>
                  <h2 className="font-roboto text-sm font-semibold text-[#10324F] leading-5">
                    {item.title}
                  </h2>

                  <p className="mt-1 font-roboto text-xs  leading-5 text-[#10324F]">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-[#FFFFFF] py-10  px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto mt-4 mb-4">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl sm:text-3xl font-bold uppercase">
              Simple, Fast & Reliable Employee Communication
            </h2>

            <p className="mt-4 font-roboto font-normal text-base leading-6 text-center text-[#10324F]">
              Create, publish, and deliver company announcements instantly while
              ensuring employees receive timely workplace updates.
            </p>
          </div>

          {/* Steps */}
          <div className="mt-14">
            <div className="grid grid-cols-1 sm:grid-cols-5 lg:grid-cols-5 sm:gap-8 gap-0">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  {/* Card */}
                  <div
                    className="relative bg-white rounded-2xl border border-[#E6EEF5]
          p-6 sm:min-h-75 lg:min-h-61"
                    style={{
                      boxShadow: `
              rgb(167 191 210 / 35%) -15px -15px 20px inset,
              rgb(170 188 202 / 20%) -20px -20px 30px
            `,
                    }}
                  >
                    <h2 className="text-xs sm:text-sm lg:text-lg font-bold text-[#10324F] sm:whitespace-pre-line">
                      {step.title}
                    </h2>

                    <p className="mt-5 text-xs leading-6 text-[#10324F]">
                      {step.desc}
                    </p>
                  </div>

                  {/* Arrow overlaps card */}
                  {index !== steps.length - 1 && (
                    <div
                      className="
              hidden sm:flex
              absolute
              top-1/2
              -right-10
              -translate-y-1/2
              z-20
            "
                    >
                      <svg
                        width="52"
                        height="40"
                        viewBox="0 0 52 44"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <linearGradient
                            id={`arrowGradient${index}`}
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="1"
                          >
                            <stop offset="0%" stopColor="#D3DCE5" />
                            <stop offset="45%" stopColor="#95A7B7" />
                            <stop offset="100%" stopColor="#5A6F84" />
                          </linearGradient>
                        </defs>

                        <path
                          d="M0 10H28V0L52 22L28 44V34H0V10Z"
                          fill={`url(#arrowGradient${index})`}
                        />
                      </svg>
                    </div>
                  )}
                  {index !== steps.length - 1 && (
                    <div className="flex sm:hidden justify-center">
                      <svg
                        width="44"
                        height="44"
                        viewBox="0 0 52 44"
                        className="rotate-90"
                      >
                        <defs>
                          <linearGradient
                            id={`mobileArrow${index}`}
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="1"
                          >
                            <stop offset="0%" stopColor="#D3DCE5" />
                            <stop offset="45%" stopColor="#95A7B7" />
                            <stop offset="100%" stopColor="#5A6F84" />
                          </linearGradient>
                        </defs>

                        <path
                          d="M0 10H28V0L52 22L28 44V34H0V10Z"
                          fill={`url(#mobileArrow${index})`}
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* <div className="mt-14 flex flex-col lg:flex-row items-center justify-center gap-5">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
             
                <div
                  className="
                        w-full
                        bg-white
                        rounded-2xl
                        p-6
                        transition-all
                        duration-300
                        min-h-38
                        flex flex-col justify-start
                      "
                  style={{
                    boxShadow: `
            rgb(167 191 210 / 40%) -15px -15px 20px inset,
            rgb(170 188 202 / 30%) -20px -20px 30px
          `,
                  }}
                >
                 

                
                  <h2 className="mt-2 text-[#10324F] text-lg font-semibold">
                    {step.title}
                  </h2>

               
                  <p className="mt-3 service-list-item-text">{step.desc}</p>
                </div>

                {index !== steps.length - 1 && (
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    width="100"
                    height="50"
                    viewBox="0 0 52 44"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="rotate-90 md:rotate-0"
                  >
                    <defs>
                      <linearGradient
                        id="arrowGradient"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#B8C4D1" />
                        <stop offset="100%" stopColor="#6E8194" />
                      </linearGradient>
                    </defs>

                    <path
                      d="M0 10H28V0L52 22L28 44V34H0V10Z"
                      fill="url(#arrowGradient)"
                    />
                  </svg>
                )}
              </React.Fragment>
            ))}
          </div> */}
        </div>
      </section>
      <Suspense fallback={null}>
        <section className="bg-[#F6F9FB] py-10 px-6 overflow-hidden">
          <div className="max-w-6xl mx-auto mt-6 mb-10">
            <div className="grid lg:grid-cols-[42%_58%] gap-4 lg:gap-0 items-center">
              {/* Left Content */}
              <div className="sm:w-175 sm:-top-5 sm:relative">
                <h2 className="text-[#10324F] font-bold text-xl md:text-3xl leading-[1.4] mb-6">
                  Why Businesses Choose Waggex
                  <br /> for Internal Communication
                </h2>

                <p className="text-[#10324F] text-base md:text-lg leading-9 max-w-md items-start">
                  Missed announcements and scattered communication lead to
                  confusion and repeated follow-ups. Waggex centralizes employee
                  communication, ensuring important updates are delivered
                  clearly, consistently, and on time
                </p>
              </div>

              {/* Right Side Cards */}
              <div className="relative">
                <div className="flex flex-col gap-5">
                  {/* First Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:w-[66%] lg:ml-auto">
                    {journeySteps.slice(0, 2).map((step) => (
                      <JourneyCard key={step.id} step={step} />
                    ))}
                  </div>

                  {/* Second Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {journeySteps.slice(2).map((step) => (
                      <JourneyCard key={step.id} step={step} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="py-10 px-6 bg-white">
          <div className="max-w-6xl mx-auto mt-1 mb-10">
            {/* Heading */}
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-bold text-[#10324F] uppercase">
                One Platform. Every HR Function Connected
              </h2>

              <p className="mt-5 text-[#10324F] text-lg leading-8">
                Connect Company Announcements with payroll, attendance, leave
                management, FaceLens, employee records, compliance, and other
                HRMS modules to manage communication and workforce operations
                from one centralized platform
              </p>
            </div>

            {/* Modules */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-20 max-w-4xl mx-auto p-8 sm:p-0">
              {hrmsModules.map((module, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 group cursor-pointer"
                >
                  {/* Icon */}
                  <span className="font-roboto text-[#10324F] font-bold">
                    ▶
                  </span>

                  {/* Text */}
                  <span className="text-lg font-medium text-[#10324F] group-hover:text-[#0B2740] transition">
                    {module}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
        <CustomerReviews />
        {/* <FAQ /> */}
        <HomeFaq
          faq1={Faqs1}
          faq2={Faqs2}
          subtitle="Common questions from Indian businesses evaluating Waggex GPS Attendance Software"
        />
        <PayrollBanner
          heading="Transform the Way You Communicate with Employees"
          description="Replace scattered emails and chats with one centralized platform for company announcements, policy updates, payroll notices, and employee communication"
          buttonText="Try For Free"
        />
        <AppDownloadCard />
        <ModernizeHR
          heading="Simplify Payroll Processing With Waggex"
          description="Run accurate, compliant payroll every month using payroll management software built for the way Indian businesses actually operate. No spreadsheets. No errors."
        />
      </Suspense>
    </>
  );
}
